<template>
    <div class="grid">
        <div class="unit whole">
            <h1>Allocate teachers</h1>
            <p class="lead">Allocate teachers to classes.</p>
        </div>
    </div>
    <div v-if="teachers.length > 0" class="grid">
        <div class="unit whole">
            <div class="panel">
                <v-card :disabled="selectedRows.length == 0" title="Multiple add or remove" class="my-3">
                    <v-card-subtitle>
                        <span v-if="selectedRows.length == 0">
                            Select teachers to add and remove them from classes.
                        </span>
                        <span v-else>
                            Select the classes to add and remove the {{ selectedRows.length }} selected teacher{{
                                selectedRows.length > 1 ? 's' : ''
                            }}
                            to/from.</span
                        >
                    </v-card-subtitle>
                    <v-card-text>
                        <div class="d-flex">
                            <v-select
                                v-model="addToClasses"
                                label="Add to classes"
                                :items="classes"
                                item-title="name"
                                item-value="uid"
                                density="compact"
                                hide-details
                                multiple
                                chips
                                class="mr-2"
                            >
                                <template #chip="{ item: c }">
                                    <v-chip :text="c.title" :color="colorNames[c.value % colorNames.length]"></v-chip>
                                </template>
                            </v-select>

                            <v-select
                                v-model="removeFromClasses"
                                label="Remove from classes"
                                :items="classes"
                                item-title="name"
                                item-value="uid"
                                density="compact"
                                hide-details
                                multiple
                                chips
                            >
                                <template #chip="{ item: c }">
                                    <v-chip :text="c.title" :color="colorNames[c.value % colorNames.length]"></v-chip>
                                </template>
                            </v-select>
                        </div>
                    </v-card-text>
                    <v-card-actions>
                        <a class="btn ml-2" :class="{ disabled: selectedRows.length == 0 }" @click="updateClassesBulk"
                            >Save</a
                        >
                    </v-card-actions>
                </v-card>
                <div class="d-flex justify-end">
                    <div class="w-25">
                        <v-select
                            v-model="filterClass"
                            label="Filter by class"
                            :items="classes"
                            item-title="name"
                            item-value="uid"
                            clearable
                            density="compact"
                            hide-details
                        >
                        </v-select>
                    </div>
                </div>
                <v-data-table
                    v-model="selectedRows"
                    v-model:sort-by="sortBy"
                    show-select
                    density="compact"
                    items-per-page="-1"
                    :headers="headers"
                    :items="items"
                    item-value="uid"
                    class="elevation-0"
                >
                    <template #bottom></template>
                    <template #[`item.classes`]="{ item }">
                        <v-select
                            v-model="item.classes"
                            class="my-2"
                            :items="classItems"
                            item-title="name"
                            item-value="uid"
                            chips
                            color="blue-grey-lighten-2"
                            label="Select classes"
                            multiple
                            hide-details
                            return-object
                            @update:model-value="updateClasses(item, item.classes)"
                        >
                            <template #chip="{ item: c }">
                                <v-chip :text="c.title" :color="colorNames[c.value % colorNames.length]"></v-chip>
                            </template>
                        </v-select>
                    </template>
                </v-data-table>
            </div>
        </div>
    </div>
    <v-overlay
        :model-value="!isLoaded.teachers || !isLoaded.classes"
        class="align-center justify-center"
        scroll-strategy="none"
    >
        <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
    </v-overlay>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { VDataTable } from 'vuetify/components/VDataTable';
import { TeacherOverview } from '@ilteducation/giglets-types';

definePage({ meta: { area: 'teachersnav' } });

const confirm = useConfirmDialog();
const { snackbarSuccess } = useSnackbar();

const store = useAdminStore();
store.fetchTeachers();
store.fetchClasses();
const { teachers, classes, isLoaded } = storeToRefs(store);

const filterClass = ref<string>();
const addToClasses = ref<string[]>([]);
const removeFromClasses = ref<string[]>([]);
const sortBy = ref<VDataTable['sortBy']>([{ key: 'name', order: 'asc' }]);
const selectedRows = ref<Array<string>>([]);

watch(selectedRows, (newValue, oldValue) => {
    if (oldValue.length === 1 && newValue.length === 2) {
        // clear before showing the card
        addToClasses.value = [];
        removeFromClasses.value = [];
    }
});

const classItems = computed(() => {
    return classes.value.map((c) => ({ uid: c.uid, name: c.name }));
});

const items = computed(() =>
    teachers.value.filter((t) => !filterClass.value || t.classes.map((c) => c.uid).includes(filterClass.value)),
);

async function updateClasses(teacher: TeacherOverview, newClasses: { uid: string; name: string }[]) {
    const newClassIds = newClasses.map((c) => c.uid);
    // To find the class the teacher has been added to, search through the school's classes:
    // * look only at the classes in newClasses
    // * find the class where the teacher isn't a member
    //
    // To find the class the teacher has been removed from, search through the school's classes:
    // * look only at the classes NOT in newClasses
    // * find the class where the teacher is a member
    const addedToClass = classes.value
        .filter((c) => newClassIds.includes(c.uid))
        .find((c) => !c.teachers.map((p) => p.id).includes(teacher.id));
    const removedFromClass = classes.value
        .filter((c) => !newClassIds.includes(c.uid))
        .find((c) => c.teachers.map((p) => p.id).includes(teacher.id));

    if (addedToClass) {
        await store.updateClass(addedToClass.uid, { addTeachers: [teacher.uid] });
    }
    if (removedFromClass) {
        await store.updateClass(removedFromClass.uid, { removeTeachers: [teacher.uid] });
    }
    snackbarSuccess('Teacher classes have been updated.');
}

async function updateClassesBulk() {
    const res = await confirm(
        'Update classes',
        `Are you sure you want to update the classes of the ${selectedRows.value.length} selected teachers?`,
    );
    if (res) {
        await Promise.all(
            [
                addToClasses.value.map((classUid) => {
                    return store.updateClass(classUid, { addTeachers: selectedRows.value });
                }),
                removeFromClasses.value.map((classUid) => {
                    return store.updateClass(classUid, { removeTeachers: selectedRows.value });
                }),
            ].flat(),
        );
        snackbarSuccess('Teacher classes have been updated.');
        await store.fetchTeachers();
        await store.fetchClasses();
        addToClasses.value = [];
        removeFromClasses.value = [];
        selectedRows.value = [];
    }
}

const colorNames = ['red', 'pink', 'purple', 'indigo', 'blue', 'cyan', 'teal', 'green', 'orange', 'brown'];

const headers = [
    {
        title: 'Name',
        key: 'name',
        sortable: true,
    },
    {
        title: 'Classes',
        key: 'classes',
        sortable: false,
    },
];
</script>
<style scoped lang="scss">
:deep(th) {
    vertical-align: revert;
}
:deep(td) {
    text-align: revert;
    vertical-align: revert;
    border-top: revert;
}
</style>
